import { itemTemplateApi } from "#/api/itemTemplateApi";
import { Button, Modal, Select, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

interface SelectTemplateModalProps {
  opened: boolean;
  onClose: () => void;
}

const SelectTemplateModal = ({ opened, onClose }: SelectTemplateModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canWrite } = usePermission();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const { data: { templates = [] } = {}, isLoading: templatesLoading } = useQuery({
    queryKey: ["templates-for-items"],
    queryFn: async () => {
      const response = await itemTemplateApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch templates");
      }
      return {
        templates: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const handleClose = () => {
    setSelectedTemplateId(null);
    onClose();
  };

  const handleContinue = () => {
    if (selectedTemplateId) {
      void navigate(`/inventory/items/new?template=${selectedTemplateId}`);
      handleClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={t("items.selectTemplate.title")}
      closeOnClickOutside={false}
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        {templatesLoading ? (
          <Text size="sm" c="dimmed">{t("items.selectTemplate.loadingTemplates")}</Text>
        ) : templates.length === 0 ? (
          <div className="flex flex-col gap-2">
            <Text size="sm" c="dimmed">
              {t("items.selectTemplate.noTemplatesFound")}
            </Text>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                variant="outline"
                color="black"
                disabled={!canWrite()}
                onClick={() => {
                  handleClose();
                  void navigate("/inventory/templates/new");
                }}
              >
                {t("items.selectTemplate.createTemplate")}
              </Button>
            </PermissionTooltip>
          </div>
        ) : (
          <>
            <Select
              label={t("items.selectTemplate.templateLabel")}
              placeholder={t("items.selectTemplate.templatePlaceholder")}
              searchable
              data={templates.map((template) => ({
                label: template.name,
                value: template.id.toString(),
              }))}
              value={selectedTemplateId}
              onChange={setSelectedTemplateId}
              description={
                selectedTemplateId
                  ? templates.find((t) => t.id.toString() === selectedTemplateId)
                      ?.description || t("items.selectTemplate.noDescription")
                  : t("items.selectTemplate.chooseTemplate")
              }
            />

            <Button
              color="black"
              fullWidth
              disabled={!selectedTemplateId}
              onClick={handleContinue}
            >
              {t("common.continue")}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default SelectTemplateModal;
